import { cn } from '@/lib/utils'

export type TriState = '' | 'true' | 'false'

export interface TriStateFilterProps {
    label: string
    value: TriState
    onChange: (next: TriState) => void
    /** Overrides the yes/no wording, e.g. `['Hidden', 'Visible']`. */
    labels?: [yes: string, no: string]
    className?: string
}

/**
 * Any / yes / no as a segmented control.
 *
 * A checkbox cannot express the third state, which is the one these filters exist
 * for -- "only the resolved ones" and "only the unresolved ones" are different
 * questions, and neither is "don't care".
 */
export function TriStateFilter({ label, value, onChange, labels = ['Yes', 'No'], className }: TriStateFilterProps) {
    const options: { value: TriState; label: string }[] = [
        { value: '', label: 'Any' },
        { value: 'true', label: labels[0] },
        { value: 'false', label: labels[1] }
    ]

    return (
        <div className={cn('flex items-center gap-1.5', className)}>
            <span id={`tri-${label}`} className="text-xs text-muted-foreground">
                {label}
            </span>
            <div
                role="radiogroup"
                aria-labelledby={`tri-${label}`}
                // Three segments with no gap between them is the densest cluster in the
                // kit, so the track and its options grow on a coarse pointer. Hit-slop
                // is not an option here: neighbouring slops would overlap completely.
                className="inline-flex h-7 items-center rounded-md border border-input bg-surface-raised p-0.5 pointer-coarse:h-10">
                {options.map(option => {
                    const selected = option.value === value
                    return (
                        <button
                            key={option.value || 'any'}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            onClick={() => onChange(option.value)}
                            // Segmented idiom: the selected option is a raised card chip,
                            // cued by fill, weight and a hairline ring rather than colour.
                            className={cn(
                                'rounded-[4px] px-2 py-px text-xs leading-5 transition-colors duration-(--animate-duration-fast)',
                                // Fills the 40px track (34px inside its border and padding)
                                // and widens each option to roughly 48px.
                                'pointer-coarse:h-8 pointer-coarse:px-3',
                                'outline-none focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring',
                                selected
                                    ? 'bg-card font-medium text-foreground shadow-xs ring-1 ring-border'
                                    : 'font-normal text-muted-foreground hover:text-foreground'
                            )}>
                            {option.label}
                        </button>
                    )
                })}
            </div>
        </div>
    )
}
